import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { Card, CardContent, Link, Typography, CircularProgress, Grid, Tooltip, Button } from '@mui/material'
import { STATUS } from 'common/constants/storeConstants'
import { uniq, compact, partition, countBy, orderBy, sortBy, uniqBy } from 'lodash'
import useTitles from 'common/hooks/useTitles'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { VersionsActions, selectVersions } from 'versions/versionsSlice'
import { CardTitle } from 'common/components/card/Card'
import useActions from 'common/hooks/useActions'
import useLocalStorage from 'common/hooks/useLocalStorage'
import { OTHER } from 'common/components/action/ActionConstant'
import { darkBlue } from 'app/theme'
import { getDateWithNumVersion, getFullDateMini } from 'common/utils/DateUtil'
import TabsSideBar from 'common/components/tab/TabsSideBar'
import { VersionsTable } from './VersionsTable'
import { HEADERS } from 'common/constants/VersionsConstants'
import { removePipes, truncateString } from 'common/utils/StringUtil'
import PropTypes from 'prop-types'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import useFetch from 'common/hooks/useFetch'
import ApplicationConf from 'conf/ApplicationConf'
import VersionsStatsCards from './VersionsStatsCards'
import PostReleaseBugsDialog from './PostReleaseBugsDialog'
import { computePostReleaseBugs, computeAverages } from './postReleaseBugsUtil'

const FilterVersion = ({
    tickets = [],
    filters = {},
    setFilters = () => {},
}) => {
    const { searchValue = [], project = [], reporter = [], handler = [] } = filters

    return (
        <Card>
            <CardTitle title='Filtre' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={6}>
                        <Input
                            label='Rechercher'
                            value={searchValue}
                            onChange={v => setFilters({ ...filters, searchValue: v })}
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            multiple
                            label='Projet'
                            value={project}
                            items={sortBy(uniqBy(tickets, 'project').map(t => ({ value: t.project, label: t.project })), 'value')}
                            onChange={v => setFilters({ ...filters, project: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            multiple
                            label='Rapporteur'
                            value={reporter}
                            items={sortBy(uniqBy(tickets, 'reporter').map(t => ({ value: t.reporter, label: t.reporter })), 'value')}
                            onChange={v => setFilters({ ...filters, reporter: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid size={6}>
                        <Select
                            multiple
                            label='Assigné à'
                            value={handler}
                            items={sortBy(uniqBy(tickets, 'handler').map(t => ({ value: t.handler, label: t.handler })), 'value')}
                            onChange={v => setFilters({ ...filters, handler: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid container size={12} justifyContent='flex-end'>
                        <Button variant='outlined' onClick={() => setFilters({})}>Réinitialiser</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>)
}

FilterVersion.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    filters: PropTypes.object,
    setFilters: PropTypes.func,
}

const VersionsApp = () => {
    useTitles(() => [{
        label: 'Versions',
        href: '/versions',
    }], [])

    const dispatch = useDispatch()

    const rawTickets = useSelector(selectVersions, shallowEqual)
    const tickets = useMemo(() => rawTickets ?? [], [rawTickets])
    const status = useSelector((s) => s.supervision.versions.status)

    const [filters, setFilters] = useState({})

    const filterTickets = useMemo(() => {
        const { searchValue, project, reporter, handler } = filters
        const searchValueFiltered = searchValue ? tickets.filter(t => {
            const searchField = `${t.id} ${t.project} ${t.product} ${t.summary} ${t.balises} ${t.handler} ${t.reporter} ${t.changeLog}`.toLocaleLowerCase()
            return searchField.includes(searchValue.toLocaleLowerCase())
        }) : tickets
        const projectFiltered = project?.length ? searchValueFiltered.filter(t => project.includes(t.project)) : searchValueFiltered
        const reporterFiltered = reporter?.length ? projectFiltered.filter(t => reporter.includes(t.reporter)) : projectFiltered
        const handlerFiltered = handler?.length ? reporterFiltered.filter(t => handler.includes(t.handler)) : reporterFiltered

        return handlerFiltered
    }, [tickets, filters])


    useEffect(() => {
        dispatch(VersionsActions.fetch())
    }, [dispatch])

    const reload = useCallback(() => {
        dispatch(VersionsActions.reset())
        dispatch(VersionsActions.fetch())
    }, [dispatch])

    useActions(() => [{
        type: OTHER,
        icon: 'cached',
        tooltip: 'Actualiser',
        onClick: reload,
    }], [reload])

    const versionCounts = useMemo(() => countBy(filterTickets, 'version'), [filterTickets])
    const filteredVersions = useMemo(() => uniq(compact(filterTickets.map(({ version }) => version))), [filterTickets])

    // Bugs critiques post-release : on récupère les tickets Anomalie créés depuis la plus ancienne
    // release, puis on compte côté client ceux en priorité P1+P2 tombant dans la fenêtre de chaque version.
    const anomaliesBody = useMemo(() => {
        const releases = filteredVersions.map(getDateWithNumVersion).filter(Number.isFinite)
        return {
            categories: ['Anomalie'],
            startDate: releases.length ? Math.floor(Math.min(...releases) / 1000) : undefined,
            endDate: Math.floor(Date.now() / 1000),
        }
    }, [filteredVersions])

    const { data: anomalies = [] } = useFetch(
        ApplicationConf.support.statisticsCreated(),
        { method: 'POST', body: anomaliesBody },
        { formatResult: (list) => (list || []).map(r => ({
            id: r.id, summary: r.summary, status: r.status, category: r.category, project: r.project,
            dateSubmitted: r.dateSubmitted, lastUpdated: r.lastUpdated, dueDate: r.dueDate,
            reporter: r.reporter, handler: r.handler, lot: r.lot, avancement: r.avancement,
            chiffrage: r.chiffrage, tempspasse: r.tempspasse, priority: r.priority, severity: r.severity,
        })) },
    )

    const bugStats = useMemo(() => computePostReleaseBugs(filteredVersions, anomalies), [filteredVersions, anomalies])
    const bugCountByVersion = useMemo(() => bugStats.reduce((acc, s) => ({ ...acc, [s.version]: s.count }), {}), [bugStats])
    const bugTicketsByVersion = useMemo(() => bugStats.reduce((acc, s) => ({ ...acc, [s.version]: s.tickets }), {}), [bugStats])
    const averages = useMemo(() => computeAverages(bugStats), [bugStats])

    const [postReleaseDialogVersion, setPostReleaseDialogVersion] = useState(undefined)

    const [selectedVersion, setSelectedVersion] = useLocalStorage('versions:selected', '')
    const [visibleVersions, setVisibleVersions] = useState([])

    useEffect(() => {
        if (!filteredVersions.length) {
            setVisibleVersions([])
            setSelectedVersion('')
            return
        }
        const isNumericVersion = (v) => /^\d+(\.\d+)*$/.test(String(v).trim())
        const [numeric, nonNumeric] = partition(filteredVersions, isNumericVersion)
        const numericSorted = orderBy(numeric, [(v) => v], ['desc'])
        const nonNumericSorted = orderBy(nonNumeric, [(v) => v], ['asc'])
        const ordered = [...numericSorted, ...nonNumericSorted]
        setVisibleVersions(ordered)
        setSelectedVersion((prev) => (prev && filteredVersions.includes(prev) ? prev : ordered[0] || ''))
    }, [filteredVersions, setSelectedVersion])

    const ticketsForSelectedVersion = useMemo(() =>
        (selectedVersion ? filterTickets.filter((t) => t.version === selectedVersion) : filterTickets)
    , [filterTickets, selectedVersion])

    const rows = useMemo(() =>
        (ticketsForSelectedVersion || []).map((t) => {
            const summaryWithToolTip = (
                <Tooltip title={t.summary}>
                    {truncateString(t.summary, 50)}
                </Tooltip>
            )

            const changeLogWithToolTip = t.changeLog ? (
                <Tooltip title={t.changeLog}>
                    {truncateString(t.changeLog, 30)}
                </Tooltip>
            ) : t.changeLog

            return {
                id: t.id,
                idIssue: {
                    value: (
                        <Link target='_blank' href={`https://support.aquasys.fr/view.php?id=${t.id}`} underline='hover'>
                            {t.id}
                        </Link>
                    ),
                    sortValue: t.id,
                },
                summary: { value: summaryWithToolTip, sortValue: t.summary },
                project: { value: t.project },
                reporter: { value: t.reporter },
                handler: { value: t.handler },
                submittedAt: {
                    value: t.dateSubmitted ? getFullDateMini(t.dateSubmitted * 1000) : '-',
                    sortValue: t.dateSubmitted ?? 0,
                },
                product: { value: removePipes(t.product) },
                balises: { value: t.balises },
                changeLog: { value: changeLogWithToolTip, sortValue: t.changeLog },
            }
        }),
    [ticketsForSelectedVersion],
    )

    const noVersions = filteredVersions.length === 0

    return (
        <>
            <Grid container spacing={1}>
                <Grid size={9}>
                    <FilterVersion
                        tickets={tickets}
                        filters={filters}
                        setFilters={setFilters}
                    />
                </Grid>
                <Grid size={3}>
                    <VersionsStatsCards averages={averages} />
                </Grid>
                {noVersions ? (
                    <Grid size={12}>
                        <Card>
                            <CardTitle title='Versions' color={darkBlue} />
                            <CardContent>
                                {status === STATUS.LOADING ? (
                                    <Grid container size={12} justifyContent='center' alignItems='center' spacing={1} sx={{ padding: '5 0' }}>
                                        <CircularProgress size={20} />
                                        <Typography variant='h6' fontWeight='bold'>Chargement de versions ...</Typography>
                                    </Grid>
                                ) : (
                                    <Typography color='text.secondary'>Aucune version disponible.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ) : (
                    <Grid container size={12}>
                        <TabsSideBar
                            defaultTab={selectedVersion}
                            tabs={visibleVersions.map(v => {
                                return {
                                    id: v,
                                    title: v,
                                    date: getDateWithNumVersion(v),
                                    icon: 'label',
                                    count: versionCounts[v] || 0,
                                    bugCount: bugCountByVersion[v],
                                    content: (
                                        <VersionsTable
                                            title={`Version ${v}`}
                                            rows={rows}
                                            status={status}
                                            onRowClick={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`) }
                                            headers={HEADERS}
                                            postReleaseBugCount={bugCountByVersion[v]}
                                            onShowPostReleaseBugs={() => setPostReleaseDialogVersion(v)}
                                        />
                                    ),
                                }
                            })}
                            onChangeTab={setSelectedVersion}
                        />
                    </Grid>
                )}
            </Grid>
            <PostReleaseBugsDialog
                open={postReleaseDialogVersion !== undefined}
                onClose={() => setPostReleaseDialogVersion(undefined)}
                version={postReleaseDialogVersion ?? ''}
                tickets={postReleaseDialogVersion ? (bugTicketsByVersion[postReleaseDialogVersion] ?? []) : []}
            />
        </>
    )
}

export default VersionsApp